import api from "../../utils/api";

// NZSC-3221 — 궁금한점 답변 전수검수.
// api-admin 신규 엔드포인트:
//   GET   /api/v1/report-qna/admin/list?status=&page=&size=  (PageResponse)
//   PATCH /api/v1/report-qna/admin/:qna_seq { answer, review_status:'DONE', order_seq }
//   POST  /api/v1/report-qna/admin/:qna_seq/regenerate  (body 없음) — FAILED 건 답변 재생성
// 응답 봉투는 { code, message, data } (기존 컨벤션 동일, code === 0 성공).
// 검수완료(PATCH)는 단일 트랜잭션으로 답변확정 + 리포트 승인(report_yn=1) + 승인이력까지 처리한다.
// 재생성(POST)은 cold 에 비동기 재생성을 요청 → 성공 시 review_status 가 PENDING 으로 돌아오고
//   답변이 새로 채워진다(즉시 반영 아님 → 잠시 후 재조회 안내).

export const state = {
  _items: [],
  _page: {
    page: 1,
    size: 30,
    totalCount: 0,
    totalPage: 0,
  },
  // 사이드바 배지용 — 검수대기 건수.
  _pendingCount: 0,
};

export const mutations = {
  setReportQnaList(state, { items, page }) {
    state._items = items || [];
    state._page = page || { page: 1, size: 30, totalCount: 0, totalPage: 0 };
  },
  setReportQnaPendingCount(state, count) {
    state._pendingCount = count || 0;
  },
};

export const actions = {
  // 검수 목록(페이지드). status 미지정 시 전체(PENDING + DONE).
  async acReportQnaList({ commit }, { status, page = 1, size = 30 } = {}) {
    const url = "/api/v1/report-qna/admin/list";
    try {
      const res = await api.get(url, {
        params: { status, page, size },
      });
      if (res.data?.code === 0 && res.data.data) {
        const d = res.data.data;
        commit("setReportQnaList", {
          items: d.items,
          page: {
            page: d.page ?? page,
            size: d.size ?? size,
            totalCount: d.totalCount ?? 0,
            totalPage: d.totalPage ?? 0,
          },
        });
        // NZSC-3250: 신규 검수 메뉴 ListView 가 직접 소비할 수 있게 데이터도 반환.
        return { items: d.items ?? [], totalCount: d.totalCount ?? 0 };
      }
      return null;
    } catch (error) {
      console.log("acReportQnaList error", error?.data || error);
      return false;
    }
  },
  // 사이드바 배지 — 검수대기(PENDING) 건수만 별도 조회(size=1로 totalCount만 사용).
  // NZSC-3250: 배지 기준을 목록 [검수 대기] 탭과 동일하게(NEED_REVIEW=1차 통과된 검수가능 건만) — 6vs4 불일치 픽스
  async acReportQnaPendingCount({ commit }, { status = "NEED_REVIEW" } = {}) {
    const url = "/api/v1/report-qna/admin/list";
    try {
      const res = await api.get(url, {
        params: { status, page: 1, size: 1 },
      });
      if (res.data?.code === 0 && res.data.data) {
        commit("setReportQnaPendingCount", res.data.data.totalCount ?? 0);
        return true;
      }
      return false;
    } catch (error) {
      console.log("acReportQnaPendingCount error", error?.data || error);
      return false;
    }
  },
  // 리포트 상세(DetailView) 연계용 — 특정 order_seq 에 걸린 궁금한점 검수 항목 1건 조회.
  // ⚠ 단건/필터 GET 미제공이라 list(status별)를 받아 orderSeq 로 클라 매칭한다(확인필요: BE에
  //   report_seq/order_seq 필터 또는 단건 GET 추가 시 이 액션을 교체해야 함).
  async acReportQnaByOrder(_, { orderSeq }) {
    if (orderSeq == null) return null;
    const url = "/api/v1/report-qna/admin/list";
    for (const status of ["PENDING", "DONE", "FAILED"]) {
      try {
        const res = await api.get(url, { params: { status, page: 1, size: 200 } });
        if (res.data?.code === 0 && res.data.data?.items) {
          const found = res.data.data.items.find(r => Number(r.orderSeq) === Number(orderSeq));
          if (found) return found;
        }
      } catch (error) {
        console.log("acReportQnaByOrder error", error?.data || error);
      }
    }
    return null;
  },
  // 검수 저장 + 승인. order_seq 필수(서버에서 user_no 는 주입).
  // 성공 시 BE 가 답변확정 + report_yn=1 + 승인이력을 단일 트랜잭션으로 처리한다.
  // NZSC-3250: reviewStatus 'DONE'=질문 검수완료(리포트 승인) / 'SAVE'=답변만 저장([수정]).
  // NZSC-3573: editReason(검수 수정 사유, 선택) 동반. undefined 면 키 자체를 안 보내
  //   BE 가 컬럼을 건드리지 않는다. 빈 문자열이면 BE 가 NULL 로 지운다.
  async acReportQnaReview(_, { qnaSeq, answer, orderSeq, reviewStatus = "DONE", editReason }) {
    const url = `/api/v1/report-qna/admin/${qnaSeq}`;
    try {
      const body = { answer, review_status: reviewStatus };
      if (reviewStatus === "DONE") body.order_seq = orderSeq;
      if (editReason !== undefined) body.edit_reason = editReason;
      const res = await api.patch(url, body);
      return { ok: res.data?.code === 0, message: res.data?.message };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // NZSC-3250: 궁금한점 퍼널 노출 스위치 조회 (OPEN/TESTER/CLOSED).
  // NZSC-3308: key 지정 시 해당 스위치 조회 — REPORT_QNA_POST_PAY(신규 결제후 질문하기).
  //   미지정=REPORT_QNA_FUNNEL(구 스위치, 하위호환).
  async acReportQnaFunnelStatus(_, payload) {
    const url = "/api/v1/report-qna/admin/funnel-status";
    try {
      const key = payload?.key;
      const res = await api.get(key ? `${url}?key=${key}` : url);
      if (res.data?.code === 0 && res.data.data) {
        return res.data.data.configValue ?? null;
      }
      return null;
    } catch (error) {
      console.log("acReportQnaFunnelStatus error", error?.data || error);
      return null;
    }
  },
  // NZSC-3250: 궁금한점 퍼널 노출 스위치 변경. web 반영은 최대 30초+1분(캐시) 지연.
  async acReportQnaFunnelUpdate(_, { status, key }) {
    const url = "/api/v1/report-qna/admin/funnel-status";
    try {
      const res = await api.patch(url, key ? { status, key } : { status });
      return { ok: res.data?.code === 0, message: res.data?.message };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // FAILED 건 답변 재생성 요청. body 없음. cold 가 비동기로 답변을 다시 생성하며,
  //   성공 시 review_status 가 PENDING 으로 바뀌고 답변이 채워진다(즉시 반영 아님).
  async acReportQnaRegenerate(_, { qnaSeq }) {
    const url = `/api/v1/report-qna/admin/${qnaSeq}/regenerate`;
    try {
      const res = await api.post(url);
      return { ok: res.data?.code === 0, message: res.data?.message };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
};

export const getters = {};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
